(ns ui.sticker-dialogs.countdown
  "The Replicant countdown sticker dialog: a name, an end date in the
   future and one of the countdown color designs. With its
   `:sticker-dialog/countdown-date` action handler."
  (:require [storrito.date.datetime-local :as datetime-local]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.emoji-picker :as emoji-picker]
            [ui.sticker-dialogs.ui :as ui]))

(def text-id
  "sticker-countdown-text")

(def designs
  "The color designs of the countdown sticker, the first is the default."
  [{:start_background_color "#ca2ee1" :end_background_color "#5eb1ff" :digit_color "#7e0091" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#ca2ee1" :end_background_color "#ff6874" :digit_color "#a3004e" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#ed4956" :end_background_color "#fdcb5c" :digit_color "#d7670d" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#70c050" :end_background_color "#27c4f5" :digit_color "#4e9f2e" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#262626" :end_background_color "#262626" :digit_color "#262626" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#ffffff" :end_background_color "#ffffff" :digit_color "#262626" :digit_card_color "#efefefff" :text_color "#262626"}
   {:start_background_color "#a307ba" :end_background_color "#a307ba" :digit_color "#a307ba" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#ed4956" :end_background_color "#ed4956" :digit_color "#ed4956" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#fd8d32" :end_background_color "#fd8d32" :digit_color "#fd8d32" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#fdcb5c" :end_background_color "#fdcb5c" :digit_color "#d2a031" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#70c050" :end_background_color "#70c050" :digit_color "#70c050" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   {:start_background_color "#3897f0" :end_background_color "#3897f0" :digit_color "#3897f0" :digit_card_color "#ffffffcc" :text_color "#ffffff"}])

(def color-keys
  [:start_background_color :end_background_color :digit_color :digit_card_color :text_color])

(defn tomorrow
  "Midnight tomorrow as a datetime-local value, the default end of a
   countdown."
  []
  (let [now (js/Date.)]
    (datetime-local/format-date
      (js/Date. (.getFullYear now) (.getMonth now) (inc (.getDate now)) 0 0))))

(defn datetime-local-value
  "The datetime-local value of the `end_ts` of an existing countdown
   (a Date or epoch seconds), or nil."
  [end-ts]
  (cond
    (instance? js/Date end-ts) (datetime-local/format-date end-ts)
    (number? end-ts) (datetime-local/format-date (js/Date. (* 1000 end-ts)))
    (string? end-ts) end-ts
    :else nil))

(defn initial-state
  [{:keys [data]}]
  (merge (first designs)
         {:text ""
          :end_ts (tomorrow)}
         (select-keys data (conj color-keys :text))
         (when-let [end-ts (datetime-local-value (:end_ts data))]
           {:end_ts end-ts})))

(defn submit-data
  [dialog]
  (when (and (not (:error dialog))
             (datetime-local/date-in-future? (:end_ts dialog)))
    [:countdown-sticker "Countdown Sticker"
     (-> (select-keys dialog (conj color-keys :text))
         (assoc :end_ts (js/Date. (:end_ts dialog))))]))

(defn date!
  "The end of the countdown must lie in the future."
  [{:keys [store action]}]
  (let [[_ value] action]
    (actions/swap-dialog! store assoc
                          :end_ts value
                          :error (when-not (datetime-local/date-in-future? value)
                                   "Error: Date in past"))))

(defn color-palette
  [dialog]
  (let [selected (select-keys dialog [:end_background_color :start_background_color])]
    [:div.flex.gap-2.pb-4.flex-wrap
     (map (fn [{:keys [start_background_color end_background_color] :as design}]
            [:div.w-8.h-8.border-2.cursor-pointer.rounded
             {:replicant/key (str start_background_color end_background_color)
              :class (if (= selected
                            (select-keys design [:end_background_color :start_background_color]))
                       ["border-4" "border-gray-400"]
                       ["border-gray-200"])
              :style {:background (str "linear-gradient(" start_background_color ", " end_background_color ")")}
              :on {:click [[:sticker-dialog/merge design]]}}])
          designs)]))

(defn panel
  [dialog]
  [:div
   [:h1.text-4xl.mb-4 {:style {:font-family "AvenyT"}} "Add a Countdown"]
   [:form.flex.flex-col.items-center
    {:on {:submit [[:js/prevent-default]
                   [:sticker-dialog/submit]]}}
    (color-palette dialog)
    [:div.flex.items-end.relative
     (ui/text-field dialog
                    {:tag :textarea
                     :field :text
                     :dom-id text-id
                     :rows 2
                     :placeholder "COUNTDOWN NAME"
                     :autofocus? true
                     :class ["border" "border-gray-500" "rounded" "p-2" "text-2xl"]
                     :style {:word-break "break-word"
                             :font-family "AvenyT"
                             :resize "none"
                             :overflow "hidden"
                             :width "215px"}})
     (ui/emoji-button {:field :text
                       :dom-id text-id
                       :class ["absolute" "left-full" "bottom-2" "ml-2" "text-black" "text-xl"]})
     (emoji-picker/panel dialog :text)]
    [:div.mt-4.mb-5.flex.flex-col.items-center
     [:label.block.text-sm.font-medium.text-gray-700.mb-1 "Ends at"]
     [:input.border.border-gray-300.rounded.px-3.py-2
      {:type "datetime-local"
       :value (or (:end_ts dialog) "")
       :min (datetime-local/current-datetime-local)
       :on {:input [[:sticker-dialog/countdown-date :event/target.value]]}}]
     [:div.text-red-600.mt-1 (:error dialog)]]
    [:div.mt-4.justify-center.w-full.flex
     (ui/primary-button {:type "submit"
                         :disabled (or (some? (:error dialog))
                                       (ui/blank? (:text dialog))
                                       (not (datetime-local/date-in-future? (:end_ts dialog))))}
                        "Add Countdown")]]])

(defn render
  [state]
  (when-let [dialog (actions/dialog state)]
    (panel dialog)))

(def modal
  {:modal-id :modals/editor2-countdown-modal
   :on-load (fn [_state] [[:sticker-dialog/init :countdown]])
   :sticker/kind :countdown
   :sticker/single-asset-type :countdown-sticker
   :sticker/initial-state #'initial-state
   :sticker/submit-data #'submit-data
   :render #'render})

(def register
  [modal
   (actions/handler :sticker-dialog/countdown-date date!)])
